import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import paperImage from "../assets/images/paper.png";
import badgeImg from "../assets/images/badge.png";
import checkImg from "../assets/images/check.png";

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const documentVariants: any = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 50,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300,
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: 50,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface ProposalDocumentProps {
  // Header Section
  title?: string;
  subtitle?: string;
  description?: string;

  // Proposer name (shown instead of price)
  proposerName?: string;
  proposerLabel?: string;

  // Priest Section (kept from original)
  priestName?: string;
  priestLabel?: string;

  // Proposal text (replaces input field)
  proposalText?: string;

  // Single name to display (replaces nameOptions)
  recipientName?: string;
  recipientPrompt?: string;

  // Action Buttons
  acceptButtonText?: string;
  cancelButtonText?: string;

  // Styling
  className?: string;

  // Control visibility
  isOpen?: boolean;

  // Callbacks
  onAccept?: () => void;
  onCancel?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ProposalDocument: React.FC<ProposalDocumentProps> = ({
  // Default Props
  title = "Proposal",
  subtitle = "ATLANTIS",
  description = "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
  proposerName = "Mario Boss",
  proposerLabel = "Proposer",
  priestName = "Ernesto",
  priestLabel = "Preotul",
  proposalText = "You have been proposed by Mario Boss. Do you accept his proposal?",
  recipientName = "Cristina",
  recipientPrompt = "If you accept this proposal, you will have the name:",
  acceptButtonText = "Accept",
  cancelButtonText = "Cancel",
  className = "",
  isOpen = true,
  onAccept,
  onCancel,
}) => {
  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleAccept = () => {
    onAccept?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // Sprečava zatvaranje kada klikneš na modal
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Glavni dokument */}
          <motion.div
            variants={documentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleModalClick}
            className="relative"
          >
            <BackgroundImage
              imageSrc={paperImage}
              imageClassName="!select-none !object-fill"
              className={`tms-font relative ${className}`}
            >
              <div className="relative min-w-[1000px] p-10 chakra-petch-font">
                {/* Main Content */}
                <div className="relative px-8 sm:px-12 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20">
                  {/* ============================================ */}
                  {/* HEADER SECTION */}
                  {/* ============================================ */}
                  <div className="text-center mb-6">
                    <h1 className="text-5xl sm:text-6xl md:text-7xl text-gray-800 leading-none billion-dreams-font">
                      {title}
                    </h1>
                    <h2
                      style={{ fontWeight: "bolder" }}
                      className="text-base sm:text-xl md:text-2xl text-blue-500 -mt-5 bebas-neue-font"
                    >
                      {subtitle}
                    </h2>
                  </div>

                  {/* Description Text */}
                  <p className="text-center text-gray-500 text-sm sm:text-base mb-8 max-w-md mx-auto inter-font">
                    {description}
                  </p>

                  {/* ============================================ */}
                  {/* PROPOSAL TEXT SECTION */}
                  {/* ============================================ */}
                  <div className="flex flex-col justify-center items-center">
                    {/* Proposal Text (instead of input) */}
                    <div className="rounded-[8px] border border-[#bcbcbc] bg-[#c8c8c8] w-[325px] p-3 text-black text-center">
                      {proposalText}
                    </div>

                    {/* Single Name Display (instead of radio options) */}
                    <div className="mb-10">
                      <p className="text-center text-gray-700 text-sm sm:text-base mb-2 mt-5 font-medium">
                        {recipientPrompt}
                      </p>

                      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8">
                        <div className="flex items-center gap-3">
                          {/**    
                          <div className="relative">
                            <div
                              style={{
                                background:
                                  "linear-gradient(to bottom, rgba(23,133,239,1) 0%, rgba(13,76,137,0.9) 100%)",
                              }}
                              className="w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center drop-shadow-2xl"
                            >
                              <img
                                src={checkImg}
                                loading="eager"
                                alt="check"
                                width={12}
                                height={12}
                              />
                            </div>
                          </div> */}

                          {/* Single Name Label */}
                          <span className="text-base sm:text-xl font-semibold text-gray-800">
                            {recipientName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ============================================ */}
                  {/* PROPOSER, BADGE & PRIEST SECTION (SERVICE TAX REMOVED) */}
                  {/* ============================================ */}
                  <div className="relative flex items-center justify-between max-w-[78%] space-x-16 mx-auto">
                    {/* Left: Proposer Info (replaces price) */}
                    <div className="flex-1 text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-blue-500 mb-2">
                        {proposerName}
                      </div>
                      <div className="h-[2px] w-full bg-black/20 mb-1"></div>
                      <div className="text-base sm:text-lg text-[#8b8b8b] font-semibold">
                        {proposerLabel}
                      </div>
                    </div>

                    {/* Center: Badge/Seal */}
                    <div>
                      <img
                        src={badgeImg}
                        width={500}
                        height={500}
                        alt="img"
                        className="w-[85px] object-contain"
                      />
                    </div>

                    {/* Right: Priest Info */}
                    <div className="flex-1 text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-blue-500 mb-2">
                        {priestName}
                      </div>
                      <div className="h-[2px] w-full bg-black/20 mb-1"></div>
                      <div className="text-base sm:text-lg text-[#8b8b8b] font-semibold">
                        {priestLabel}
                      </div>
                    </div>
                  </div>

                  {/* ============================================ */}
                  {/* ACTION BUTTONS */}
                  {/* ============================================ */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                    {/* Accept Button */}
                    <button
                      title="Accept"
                      onClick={handleAccept}
                      className="w-full sm:w-auto min-w-[200px] px-12 py-4 rounded-lg font-semibold text-lg transition-all duration-200 active:scale-95 border border-white/20 bg-[#bebebe] text-white hover:opacity-80"
                    >
                      {acceptButtonText}
                    </button>

                    {/* Cancel Button */}
                    <button
                      title="Cancel"
                      onClick={handleCancel}
                      className="w-full sm:w-auto min-w-[200px] px-12 py-4 bg-gradient-to-b from-blue-500 to-blue-600 text-white rounded-lg font-semibold text-lg transition-all duration-200 hover:bg-blue-600 active:scale-95 shadow-lg hover:shadow-xl hover:opacity-80"
                    >
                      {cancelButtonText}
                    </button>
                  </div>
                </div>
              </div>
            </BackgroundImage>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProposalDocument;
